import type { RouteLocationRaw, RouteRecordRaw, Router } from "vue-router";
import {
  getUserCenterRouteByName,
  normalizeLegacyLocation,
  userCenterRouteDefs,
  userCenterRouteNameSet,
} from "../../routes";

let activeRouter: Router | null = null;

function getRouteMeta(routeName = "") {
  const route = getUserCenterRouteByName(routeName);
  if (!route) {
    return {};
  }

  return {
    code: route.code,
    title: route.title,
    pageTitle: route.pageTitle,
    hiddenSider: route.hiddenSider,
    fullLayout: route.fullLayout,
  };
}

function createRouteRecord(routeName: string): RouteRecordRaw {
  const route = getUserCenterRouteByName(routeName);
  return {
    path: route?.legacyPath || "/",
    name: routeName,
    meta: getRouteMeta(routeName),
  } as RouteRecordRaw;
}

export const baseRoutes: RouteRecordRaw = {
  path: "",
  name: "Wrap",
  redirect: "/mall/home",
  children: userCenterRouteDefs
    .filter(route => route.requiresAuth !== false && !route.standalone)
    .map(route => createRouteRecord(route.name)),
};

export const mallRoutesData = userCenterRouteDefs
  .filter(route => route.legacyPath.startsWith("/mall") || route.legacyPath === "/collection/mallPackage")
  .map(route => createRouteRecord(route.name));

export const routes: RouteRecordRaw[] = userCenterRouteDefs.map(route => createRouteRecord(route.name));

export function setUserCenterRouterInstance(router: Router) {
  activeRouter = router;
}

function getRouter() {
  if (!activeRouter) {
    throw new Error("User center router is not ready yet.");
  }
  return activeRouter;
}

function navigateWithWindow(location: RouteLocationRaw) {
  if (!process.client) {
    return Promise.resolve();
  }

  if (typeof location === "string") {
    window.location.href = location;
    return Promise.resolve();
  }

  if (location && typeof location === "object" && "path" in location && location.path) {
    window.location.href = location.path;
  }

  return Promise.resolve();
}

const routerProxy = {
  push(to: RouteLocationRaw) {
    const target = normalizeLegacyLocation(to);
    return activeRouter ? getRouter().push(target) : navigateWithWindow(target);
  },
  replace(to: RouteLocationRaw) {
    const target = normalizeLegacyLocation(to);
    return activeRouter ? getRouter().replace(target) : navigateWithWindow(target);
  },
  resolve(to: RouteLocationRaw) {
    const target = normalizeLegacyLocation(to);
    return activeRouter ? getRouter().resolve(target) : ({ href: typeof target === "string" ? target : "" } as any);
  },
  hasRoute(name: string) {
    return activeRouter ? getRouter().hasRoute(name) : userCenterRouteNameSet.has(name);
  },
  addRoute() {
    return routerProxy;
  },
  removeRoute() {
    return;
  },
  beforeEach() {
    return () => {};
  },
  afterEach() {
    return () => {};
  },
} as unknown as Router;

Object.defineProperty(routerProxy, "currentRoute", {
  get() {
    return activeRouter?.currentRoute;
  },
});

export default routerProxy;
